import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { useFilters, useModals } from '@domain-group/react-hooks'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useSetState from '@hooks/useSetState'

import CreateAdAccountGroupModal from '@modals/CreateAdAccountGroupModal'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showCreateAdAccountGroupModal: () => void,
  },
}

const defaultState = {
  showCreateAdAccountGroupModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateAdAccountGroupModal: {
      closeModal: () => setState({ showCreateAdAccountGroupModal: false }),
      createAdAccountGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateAdAccountGroupModal } = state

  useLocation()

  const filtersPayload = useFilters({ persistFilters: true })

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals()

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateAdAccountGroupModal: () => {
        setState({ showCreateAdAccountGroupModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt]) // modalContext dependency required if layering modals

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateAdAccountGroupModal
          callbacks={callbacks('CreateAdAccountGroupModal', setState)}
          showModal={showCreateAdAccountGroupModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
